import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useMobile } from '../../../shared/hooks/useMobile';
import Logo from '../../../shared/components/ui/Logo';
import { MyTournamentsSection, MyTeamsSection } from '../components';
import { supabase } from '../../../shared/lib/supabase';
import { organisationService } from '../../../shared/services/organisations';

interface SidebarItem {
  id: string;
  name: string;
  icon: string;
  isActive?: boolean;
}

// Summary Page with custom in-app sidebar
const SummaryPage: React.FC = () => {
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const [activeTopNavItem] = useState('summary');
  const { signOut } = useAuth();
  const { currentOrganization } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();

  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const isSummaryPage = location.pathname === `/o/${orgIdFromPath}/roster/summary` || 
                        location.pathname === `/o/${orgIdFromPath}/roster/summary/`;
  const currentSection = (location.hash || '').replace('#', '');
  const isTournamentAccessPage = currentSection === 'tournament-access';
  const isTeamAccessPage = currentSection === 'team-access';
  const isSeasonAccessPage = currentSection === 'season-access';
  const isPlayerAccessPage = currentSection === 'player-access';

  const sidebarItems: SidebarItem[] = [
    { id: 'for-you', name: 'Teams', icon: '⭐', isActive: activeTopNavItem === 'for-you' },
    { id: 'players', name: 'Players', icon: '👥', isActive: activeTopNavItem === 'players' },
    { id: 'summary', name: 'Summary', icon: '📊', isActive: activeTopNavItem === 'summary' },
    { id: 'recent', name: 'Recent', icon: '🕒', isActive: activeTopNavItem === 'recent' },
    { id: 'dashboard', name: 'Go to Dashboard', icon: '🏠', isActive: false }
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch {
      navigate('/');
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId) {
      window.location.hash = `#${sectionId}`;
    } else {
      window.history.replaceState(null, '', window.location.pathname);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}`);
  };

  const handleSidebarItemClick = (itemId: string) => {
    if (itemId === 'dashboard') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}`);
    } else if (itemId === 'for-you') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster`, { replace: true });
    } else if (itemId === 'players') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster/players`, { replace: true });
    } else if (itemId === 'summary') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster/summary`, { replace: true });
    }
  };

  // Data state
  const [orgDetails, setOrgDetails] = useState<any>(null);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [tournamentAccess, setTournamentAccess] = useState<any[]>([]);
  const [teamAccess, setTeamAccess] = useState<any[]>([]);
  const [seasonAccess, setSeasonAccess] = useState<any[]>([]);
  const [playerAccess, setPlayerAccess] = useState<any[]>([]);
  const [orgTournaments, setOrgTournaments] = useState<any[]>([]);
  const [orgTeams, setOrgTeams] = useState<any[]>([]);

  const [loadingStates, setLoadingStates] = useState({
    organization: true,
    users: true,
    tournaments: true,
    teams: true,
    tournamentAccess: true,
    teamAccess: true,
    seasonAccess: true,
    playerAccess: true
  });

  useEffect(() => {
    const loadData = async () => {
        if (!orgIdFromPath) return;
      
      try {
        const [orgRes, userOrgsRes] = await Promise.all([
          organisationService.getOrganisationById(orgIdFromPath),
          supabase
            .from('user_organisations')
            .select(`
              id,
              role,
              active,
              created_at,
              user: user_id ( id, email, first_name, last_name, global_role, created_at )
            `)
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true)
        ]);

        setOrgDetails(orgRes.data);
        setLoadingStates(prev => ({ ...prev, organization: false }));
        
        setOrgUsers((userOrgsRes.data || []).map((u: any) => ({
          id: u.user?.id,
          email: u.user?.email,
          first_name: u.user?.first_name,
          last_name: u.user?.last_name,
          global_role: u.user?.global_role,
          user_created_at: u.user?.created_at,
          org_role: u.role,
          org_active: u.active,
          link_created_at: u.created_at,
        })));
        setLoadingStates(prev => ({ ...prev, users: false }));

        const [
          tournamentAccessRes,
          teamAccessRes,
          seasonAccessRes,
          playerAccessRes,
          myTournamentsRes,
          myTeamsRes
        ] = await Promise.all([
          supabase
            .from('tournament_access')
            .select('id, access_type, granted_at, is_active, tournament:tournament_id(id, name, logo, country)')
            .eq('organisation_id', orgIdFromPath)
            .eq('is_active', true),
          supabase
            .from('team_access')
            .select('id, access_type, granted_at, active, team:team_id(id, name, logo, country, city, venue, website, social_media, date_established)')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('season_access')
            .select('id, access_type, granted_at, active, season:season_id(id, name, start_date, end_date)')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('player_access')
            .select('id, access_type, granted_at, active, player:player_id(id, full_name, position, nationality, kit_number, bio, profile_image, date_of_birth, social_media)')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('tournaments')
            .select('id, name, logo, country, created_at')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('teams')
            .select('id, name, logo, country, city, venue, website, social_media, date_established')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true)
        ]);

        setTournamentAccess(tournamentAccessRes.data || []);
        setLoadingStates(prev => ({ ...prev, tournamentAccess: false }));
        
        setTeamAccess(teamAccessRes.data || []);
        setLoadingStates(prev => ({ ...prev, teamAccess: false }));
        
        setSeasonAccess(seasonAccessRes.data || []);
        setLoadingStates(prev => ({ ...prev, seasonAccess: false }));
        
        setPlayerAccess(playerAccessRes.data || []);
        setLoadingStates(prev => ({ ...prev, playerAccess: false }));
        
        setOrgTournaments(myTournamentsRes.data || []);
        setLoadingStates(prev => ({ ...prev, tournaments: false }));
        
        setOrgTeams(myTeamsRes.data || []);
        setLoadingStates(prev => ({ ...prev, teams: false }));

          } catch (error) {
            console.error('Error loading roster data:', error);
          }
        };
    loadData();
  }, [orgIdFromPath]);

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && isSummaryPage && !isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Custom Sidebar */}
      {isSummaryPage && (
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative ${
          isMobile 
            ? `fixed inset-y-0 left-0 z-50 w-64 ${isSidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}`
            : isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}>
          <div className="p-3 sm:p-4 flex flex-col h-full">
            {/* Logo - Now Clickable */}
            <div className="flex items-center justify-start mb-3 sm:mb-4">
              <button
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
                title="Go to Homepage"
                aria-label="Go to Homepage"
              >
              <Logo 
                size="md" 
                variant={isSidebarCollapsed ? 'mark' : 'full'} 
                className="flex items-center space-x-2"
              />
              </button>
            </div>

            {/* Expand/Collapse Button - Positioned at the edge */}
            <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-10">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isSidebarCollapsed ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  )}
                </svg>
              </button>
            </div>

            {/* Main Section */}
            <div className="flex-shrink-0 mb-1">
              <div className="space-y-1 px-2 sm:px-3">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSidebarItemClick(item.id)}
                    className={`w-full flex items-center ${
                      isSidebarCollapsed 
                        ? 'justify-center px-2 py-1.5' 
                        : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                    } rounded-lg transition-colors min-h-44 ${
                      item.isActive 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm">{item.icon}</span>
                    {!isSidebarCollapsed && (
                      <span className="text-xs sm:text-sm font-normal">{item.name}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Access Tables Section */}
              {!isSidebarCollapsed && (
                <>
                  <div className="my-4 border-t border-gray-200" />
                  <div className="mb-2 px-2 text-[11px] uppercase tracking-wide text-gray-500">Access tables</div>
                  <div className="space-y-1 px-2 sm:px-3">
                    {[
                      { id: 'tournament-access', name: 'Tournament Access', icon: '🏆' },
                      { id: 'team-access', name: 'Team Access', icon: '🧑‍🤝‍🧑' },
                      { id: 'season-access', name: 'Season Access', icon: '🗂️' },
                      { id: 'player-access', name: 'Player Access', icon: '👤' }
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => scrollToSection(t.id)}
                        className={`w-full flex items-center ${
                          isSidebarCollapsed 
                            ? 'justify-center px-2 py-1.5' 
                            : 'space-x-2 px-2 sm:px-3 py-1.5'
                        } rounded-lg text-gray-600 hover:bg-gray-50 text-[13px]`}
                      >
                        <span className="text-sm">{t.icon}</span>
                        {!isSidebarCollapsed && (
                          <span className="text-[13px]">{t.name}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Logout Button - Sticks to bottom */}
            <div className="mt-auto pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleLogout}
                className={`w-full flex items-center rounded-lg text-left transition-colors min-h-44 ${
                  isSidebarCollapsed 
                    ? 'justify-center px-2 py-1.5' 
                    : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                } text-gray-700 hover:bg-gray-50`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {!isSidebarCollapsed && (
                  <span className="text-xs sm:text-sm font-normal">Logout</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Organization banner */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Organization</h2>
              {loadingStates.organization ? (
                <div className="text-gray-500">Loading...</div>
              ) : orgDetails ? (
                <div className="space-y-3">
                  {/* Header with logo and basic info */}
                  <div className="flex items-center space-x-3">
                    {orgDetails.logo ? (
                      <img src={orgDetails.logo} alt={orgDetails.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">🏢</div>
                    )}
                    <div className="flex-1">
                      <div className="text-base font-semibold text-gray-900">{orgDetails.name}</div>
                      <div className="text-xs text-gray-500">
                        Member since {new Date(orgDetails.created_at).toLocaleDateString()}
                        {orgDetails.subdomain && (
                          <span className="ml-2">• <span className="font-mono">{orgDetails.subdomain}</span></span>
                        )}
                      </div>
                    </div>
                    {/* Status badge in header */}
                    {orgDetails.active ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium">
                        ✓ Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium">
                        ✗ Inactive
                      </span>
                    )}
                  </div>
                  
                  {/* Additional details in compact grid */}
                  {(orgDetails.allowed_domains || orgDetails.allowed_ips || orgDetails.requires_ip_whitelisting !== undefined) && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {orgDetails.allowed_domains && (
                          <div>
                            <span className="text-xs font-medium text-gray-600 block mb-1">Allowed Domains</span>
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(orgDetails.allowed_domains) ? (
                                orgDetails.allowed_domains.map((domain: string, index: number) => (
                                  <span key={index} className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                                    {domain}
                                  </span>
                                ))
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                                  {orgDetails.allowed_domains}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {orgDetails.allowed_ips && (
                          <div>
                            <span className="text-xs font-medium text-gray-600 block mb-1">Allowed IPs</span>
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(orgDetails.allowed_ips) ? (
                                orgDetails.allowed_ips.map((ip: string, index: number) => (
                                  <span key={index} className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-mono">
                                    {ip}
                                  </span>
                                ))
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-mono">
                                  {orgDetails.allowed_ips}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {orgDetails.requires_ip_whitelisting !== undefined && (
                          <div className="sm:col-span-2">
                            <span className="text-xs font-medium text-gray-600 block mb-1">IP Whitelisting</span>
                            {orgDetails.requires_ip_whitelisting ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-50 text-yellow-700 text-xs font-medium">
                                Required
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-50 text-gray-600 text-xs font-medium">
                                Not Required
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500">No organization found.</div>
              )}
            </div>

            {/* Users */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Users</h2>
              {loadingStates.users ? (
                <div className="text-gray-500">Loading...</div>
              ) : orgUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4">User</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">First Name</th>
                        <th className="py-2 pr-4">Last Name</th>
                        <th className="py-2 pr-4">Global Role</th>
                        <th className="py-2 pr-4">Org Role</th>
                        <th className="py-2 pr-4">Active</th>
                        <th className="py-2 pr-4">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {orgUsers.map((u: any) => (
                        <tr key={u.id}>
                          <td className="py-2 pr-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-medium">
                                {(u.first_name?.[0] || u.email?.[0] || 'U').toUpperCase()}
                              </div>
                              <span className="text-gray-900 font-medium">{u.first_name || u.email}</span>
                            </div>
                          </td>
                          <td className="py-2 pr-4">{u.email || '—'}</td>
                          <td className="py-2 pr-4">{u.first_name || '—'}</td>
                          <td className="py-2 pr-4">{u.last_name || '—'}</td>
                          <td className="py-2 pr-4">{u.global_role || '—'}</td>
                          <td className="py-2 pr-4">{u.org_role || '—'}</td>
                          <td className="py-2 pr-4">{u.org_active ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-4">{u.user_created_at ? new Date(u.user_created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500">No users linked to this organization.</div>
              )}
            </div>

            {/* Sections as tabs: show Summary when no hash */}
            {(!isTournamentAccessPage && !isTeamAccessPage && !isSeasonAccessPage && !isPlayerAccessPage) && (
              <>
                <MyTournamentsSection tournaments={orgTournaments} loading={loadingStates.tournaments} />
                <MyTeamsSection teams={orgTeams} loading={loadingStates.teams} />
              </>
            )}

            {/* Tournament Access */}
            {isTournamentAccessPage && tournamentAccess.length > 0 && (
              <div id="tournament-access" className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Tournament Access</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4">Logo</th>
                        <th className="py-2 pr-4">Tournament</th>
                        <th className="py-2 pr-4">Country</th>
                        <th className="py-2 pr-4">Access</th>
                        <th className="py-2 pr-4">Granted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tournamentAccess.map((ta: any) => (
                        <tr key={ta.id} className="align-top">
                          <td className="py-2 pr-4">
                            {ta.tournament?.logo ? (
                              <img src={ta.tournament.logo} alt={ta.tournament.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">🏆</div>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-gray-900 font-medium">{ta.tournament?.name || '—'}</td>
                          <td className="py-2 pr-4">{ta.tournament?.country || '—'}</td>
                          <td className="py-2 pr-4">{ta.access_type || '—'}</td>
                          <td className="py-2 pr-4">{ta.granted_at ? new Date(ta.granted_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Team Access */}
            {isTeamAccessPage && teamAccess.length > 0 && (
              <div id="team-access" className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Team Access</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4">Logo</th>
                        <th className="py-2 pr-4">Team</th>
                        <th className="py-2 pr-4">Location</th>
                        <th className="py-2 pr-4">Venue</th>
                        <th className="py-2 pr-4">Established</th>
                        <th className="py-2 pr-4">Access</th>
                        <th className="py-2 pr-4">Links</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {teamAccess.map((ta: any) => (
                        <tr key={ta.id} className="align-top">
                          <td className="py-2 pr-4">
                            {ta.team?.logo ? (
                              <img src={ta.team.logo} alt={ta.team.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">⚽</div>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            <div className="text-gray-900 font-medium">{ta.team?.name || '—'}</div>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="text-gray-900">{ta.team?.city || '—'}</div>
                            <div className="text-xs text-gray-500">{ta.team?.country || '—'}</div>
                          </td>
                          <td className="py-2 pr-4">
                            {ta.team?.venue ? (
                              <span className="text-gray-900">{ta.team.venue}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            {ta.team?.date_established ? (
                              <span className="text-gray-900">{new Date(ta.team.date_established).getFullYear()}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                              {ta.access_type || '—'}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="space-y-1">
                              {ta.team?.website && (
                                <div className="text-xs text-gray-900">🌐 {ta.team.website}</div>
                              )}
                              {ta.team?.social_media && (
                                <div className="text-xs text-gray-900">
                                  📱 {typeof ta.team.social_media === 'object' 
                                    ? Object.entries(ta.team.social_media).map(([platform, handle]) => `${platform}: ${handle}`).join(', ')
                                    : ta.team.social_media}
                                </div>
                              )}
                              {!ta.team?.website && !ta.team?.social_media && (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Season Access */}
            {isSeasonAccessPage && seasonAccess.length > 0 && (
              <div id="season-access" className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Season Access</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4">Season</th>
                        <th className="py-2 pr-4">Start Date</th>
                        <th className="py-2 pr-4">End Date</th>
                        <th className="py-2 pr-4">Access</th>
                        <th className="py-2 pr-4">Granted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {seasonAccess.map((sa: any) => (
                        <tr key={sa.id} className="align-top">
                          <td className="py-2 pr-4 text-gray-900 font-medium">{sa.season?.name || '—'}</td>
                          <td className="py-2 pr-4">{sa.season?.start_date ? new Date(sa.season.start_date).toLocaleDateString() : '—'}</td>
                          <td className="py-2 pr-4">{sa.season?.end_date ? new Date(sa.season.end_date).toLocaleDateString() : '—'}</td>
                          <td className="py-2 pr-4">{sa.access_type || '—'}</td>
                          <td className="py-2 pr-4">{sa.granted_at ? new Date(sa.granted_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Player Access */}
            {isPlayerAccessPage && playerAccess.length > 0 && (
              <div id="player-access" className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Player Access</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4">Player</th>
                        <th className="py-2 pr-4">Details</th>
                        <th className="py-2 pr-4">Kit #</th>
                        <th className="py-2 pr-4">Bio</th>
                        <th className="py-2 pr-4">Access</th>
                        <th className="py-2 pr-4">Social Media</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {playerAccess.map((pa: any) => (
                        <tr key={pa.id} className="align-top">
                          <td className="py-2 pr-4">
                            <div className="flex items-center space-x-2">
                              {pa.player?.profile_image ? (
                                <img src={pa.player.profile_image} alt={pa.player.full_name} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                                  {pa.player?.full_name?.charAt(0) || '👤'}
                                </div>
                              )}
                              <div>
                                <div className="text-gray-900 font-medium">{pa.player?.full_name || '—'}</div>
                                <div className="text-xs text-gray-500">{pa.player?.position || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="space-y-1">
                              <div className="text-xs text-gray-900">
                                <span className="font-medium">Nationality:</span> {pa.player?.nationality || '—'}
                              </div>
                              {pa.player?.date_of_birth && (
                                <div className="text-xs text-gray-900">
                                  <span className="font-medium">DOB:</span> {new Date(pa.player.date_of_birth).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-4">
                            {pa.player?.kit_number ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                                #{pa.player.kit_number}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            {pa.player?.bio ? (
                              <div className="text-xs text-gray-900 max-w-xs truncate" title={pa.player.bio}>
                                {pa.player.bio}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                              {pa.access_type || '—'}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            {pa.player?.social_media ? (
                              <div className="text-xs text-gray-900">
                                📱 {typeof pa.player.social_media === 'object' 
                                  ? Object.entries(pa.player.social_media).map(([platform, handle]) => `${platform}: ${handle}`).join(', ')
                                  : pa.player.social_media}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty state for Season Access */}
            {isSeasonAccessPage && seasonAccess.length === 0 && (
              <div id="season-access" className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Season Access</h2>
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No seasons found</h3>
                  <p className="text-sm text-gray-500">No seasons match your current access. Contact your administrator for access.</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SummaryPage;
